import { useMemo } from "react";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useDeferredPreQuote } from "./useDeferredPreQuote";
import { useQuote } from "./useQuote";
import type {
  IEstimateOption,
  IQuoteResponse,
  QuoteAnchor,
} from "../services/api";
import { payerCodesEquivalent, resolveCashPickupOption } from "../utils/payerCode";
import { usablePersonName } from "../utils/homeGreeting";

export type PayoutCategory = "BANK" | "CASH" | "CARD" | "OTHER";

export interface UsePayoutClaimPricingOptions {
  /**
   * When false, only pre-quote / catalog data is loaded — no binding quote.
   * Use in entry screens and payment popups that share TransferPage's quote.
   */
  enableQuote?: boolean;
}

const EMPTY_OPTIONS: IEstimateOption[] = [];

const PAYOUT_CATEGORY_MAP: Record<string, PayoutCategory> = {
  BANK_ACCOUNT: "BANK",
  CASH_PICKUP: "CASH",
  PUSH_TO_CARD: "CARD",
  MOBILE_WALLET: "OTHER",
  HOME_DELIVERY: "OTHER",
};

export function categoryFromPayoutMethod(
  method: string | undefined | null
): PayoutCategory | undefined {
  if (!method) return undefined;
  return PAYOUT_CATEGORY_MAP[method.toUpperCase()] ?? "OTHER";
}

export function categoryFromPaymentType(
  type: string | undefined | null
): PayoutCategory | undefined {
  switch (type) {
    case "CARD":
      return "CARD";
    case "BANK":
      return "BANK";
    case "CASH":
      return "CASH";
    case "OTHER":
      return "OTHER";
    default:
      return undefined;
  }
}

function optionForCategory(
  options: IEstimateOption[],
  category: PayoutCategory
): IEstimateOption | undefined {
  return options.find(
    (o) => categoryFromPayoutMethod(o.payoutMethod) === category
  );
}

function availablePayoutCategories(
  options: IEstimateOption[],
  facetPayoutMethods?: Array<{ payoutMethod: string }> | null
): Set<PayoutCategory> {
  const set = new Set<PayoutCategory>();
  // Prefer pre-collapse facets so a payout method that never wins "best per
  // payer" still appears as a category chip.
  const facetMethods = facetPayoutMethods ?? [];
  if (facetMethods.length > 0) {
    for (const f of facetMethods) {
      const cat = categoryFromPayoutMethod(f.payoutMethod);
      if (cat) set.add(cat);
    }
    return set;
  }
  for (const o of options) {
    const cat = categoryFromPayoutMethod(o.payoutMethod);
    if (cat) set.add(cat);
  }
  return set;
}

export interface TransferEconomics {
  sendAmount: number;
  receiveAmount: number;
  exchangeRate: number;
  fee: number;
  totalCost: number;
  estimated: boolean;
}

export interface PayoutClaimPricing {
  amountAnchor: QuoteAnchor;
  sourceCountry: string;
  destinationCountry: string;
  sourceCurrency: string;
  destinationCurrency: string;
  fundingMethod: string;
  sendAmount: number;
  totalDebit: number;
  senderName: string | null;
  recipientHint: { firstName?: string; lastName?: string } | null;

  preQuoteLoading: boolean;
  preQuoteError: Error | null;
  routeUnavailable: boolean;
  options: IEstimateOption[];
  availableCategories: Set<PayoutCategory>;
  optionForCategory: (category: PayoutCategory) => IEstimateOption | undefined;
  bestOption: IEstimateOption | null;

  selectedOption: IEstimateOption | null;
  selectedPayoutMethod: string | null;
  capabilityKey: string | null;

  quote: IQuoteResponse | null;
  quoteLoading: boolean;
  quoteError: Error | null;
  refetchQuote: () => void;
  isExpiringSoon: boolean;
  timeRemaining: number;

  /** Indicative receive range before a payout method is chosen. */
  receiveRange: { min: number; max: number; avg: number } | null;

  economics: TransferEconomics | null;
  /** Stable string of sorted categories — safe effect dependency (unlike Set). */
  availableCategoriesKey: string;
}

/**
 * Pricing for payout-claim: send + funding fixed at origination; recipient
 * selects payout method and receives a variable receive amount.
 */
export const usePayoutClaimPricing = (
  pricingOptions: UsePayoutClaimPricingOptions = {}
): PayoutClaimPricing => {
  const { enableQuote = true } = pricingOptions;
  const { paymentState } = useStore();
  const { session } = useSessionStore();
  const config = session.data?.config;
  const linkId = session.data?.linkId;

  const sendCommitment = config?.sendCommitment;
  const sourceCountry =
    sendCommitment?.sourceCountry ?? config?.sourceCountry ?? "US";
  const destinationCountry =
    sendCommitment?.destinationCountry ?? config?.destinationCountry ?? "MX";
  const sourceCurrency =
    sendCommitment?.sendCurrency ?? config?.sourceCurrency ?? "USD";
  const destinationCurrency =
    sendCommitment?.destinationCurrency ?? config?.destinationCurrency ?? "MXN";
  // Fail closed — never invent DEBIT_CARD when commitment funding is missing.
  const fundingMethod = sendCommitment?.fundingMethod ?? "";
  const missingFunding = !sendCommitment?.fundingMethod;
  const sendAmount = sendCommitment?.sendAmount ?? 0;
  const totalDebit = sendCommitment?.totalDebit ?? sendAmount;
  const senderName = usablePersonName(config?.senderName as string | undefined) || null;
  const recipientHint = config?.recipientHint ?? null;

  const selectedCategory = categoryFromPaymentType(paymentState.payment?.type);

  // Always fetch the full deferred pre-quote (all payout SKUs). Category
  // availability and the binding capabilityKey are resolved client-side from
  // the recipient's selection (including cash payer).
  const {
    result: preQuote,
    loading: preQuoteLoading,
    error: preQuoteError,
  } = useDeferredPreQuote(missingFunding ? null : linkId, null);

  // Stabilize empty fallback so downstream memos/effects do not see a new []
  // (and a new Set) on every render while the pre-quote is still loading.
  const options = useMemo(
    () => preQuote?.options ?? EMPTY_OPTIONS,
    [preQuote?.options]
  );
  const routeUnavailable =
    missingFunding || (!!preQuote && options.length === 0);
  const categories = useMemo(
    () =>
      availablePayoutCategories(options, preQuote?.facets?.payoutMethods),
    [options, preQuote?.facets?.payoutMethods]
  );
  // Primitive dep for effects that only care about which categories exist.
  const availableCategoriesKey = useMemo(
    () => Array.from(categories).sort().join("|"),
    [categories]
  );

  const bestOption = useMemo<IEstimateOption | null>(() => {
    if (options.length === 0) return null;
    return [...options].sort((a, b) => b.receiveAmount - a.receiveAmount)[0];
  }, [options]);

  const pickOption = useMemo(
    () => (category: PayoutCategory) => optionForCategory(options, category),
    [options]
  );

  const selectedOption = useMemo<IEstimateOption | null>(() => {
    if (!selectedCategory) return null;
    // Cash pickup must pin the SKU of the chosen payer — never the first
    // CASH_PICKUP option in the list.
    if (selectedCategory === "CASH") {
      const payerId = paymentState.payment?.cashPickup?.payerId;
      const stateCode = paymentState.payment?.cashPickup?.stateCode;
      if (!payerId) return null;
      return resolveCashPickupOption(options, payerId, stateCode);
    }
    // Bank account must pin the SKU of the chosen bank (payerCode) — never the
    // first BANK_ACCOUNT option when banks vary by price.
    if (selectedCategory === "BANK") {
      const bankId = paymentState.payment?.bankAccount?.bankId;
      if (!bankId) return null;
      const bankOptions = options.filter(
        (o) => categoryFromPayoutMethod(o.payoutMethod) === "BANK"
      );
      return (
        bankOptions.find((o) => payerCodesEquivalent(o.payerCode, bankId)) ??
        null
      );
    }
    return pickOption(selectedCategory) ?? null;
  }, [
    options,
    selectedCategory,
    pickOption,
    paymentState.payment?.cashPickup?.payerId,
    paymentState.payment?.cashPickup?.stateCode,
    paymentState.payment?.bankAccount?.bankId,
  ]);

  const selectedPayoutMethod = selectedOption?.payoutMethod ?? null;
  const capabilityKey = selectedOption?.capabilityKey ?? null;

  // Payout destination cards must not be passed as quote `cardId` — that field
  // is funding-side on the API. Claim quotes go through deferred-payout/quote
  // so the sender card is resolved server-side from the origination.
  const {
    quote,
    loading: quoteLoading,
    error: quoteError,
    refetch: refetchQuote,
    isExpiringSoon,
    timeRemaining,
  } = useQuote({
    sourceCountry,
    destinationCountry,
    fundingMethod,
    payoutMethod: selectedPayoutMethod,
    capabilityKey,
    amount: sendAmount,
    currency: sourceCurrency,
    destinationCurrency,
    anchor: "send",
    linkId,
    enabled: enableQuote && Boolean(fundingMethod) && Boolean(linkId),
  });

  const receiveRange = preQuote?.summary?.receiveAmount ?? null;

  const economics = useMemo<TransferEconomics | null>(() => {
    if (quote) {
      return {
        sendAmount: quote.sendAmount,
        receiveAmount: quote.receiveAmount,
        exchangeRate: quote.exchangeRate,
        fee: quote.totalFee ?? quote.fee ?? 0,
        totalCost: quote.totalCost,
        estimated: false,
      };
    }
    // Once the recipient picks a payout method, only the binding quote is authoritative.
    if (selectedCategory) {
      return null;
    }
    const option = bestOption;
    if (option) {
      return {
        sendAmount: option.sendAmount,
        receiveAmount: option.receiveAmount,
        exchangeRate: option.customerFxRate,
        fee: option.customerFee,
        totalCost: totalDebit,
        estimated: true,
      };
    }
    return null;
  }, [quote, selectedCategory, bestOption, totalDebit]);

  return {
    amountAnchor: "send",
    sourceCountry,
    destinationCountry,
    sourceCurrency,
    destinationCurrency,
    fundingMethod,
    sendAmount,
    totalDebit,
    senderName,
    recipientHint,
    preQuoteLoading,
    preQuoteError,
    routeUnavailable,
    options,
    availableCategories: categories,
    optionForCategory: pickOption,
    bestOption,
    selectedOption,
    selectedPayoutMethod,
    capabilityKey,
    quote,
    quoteLoading,
    quoteError,
    refetchQuote,
    isExpiringSoon,
    timeRemaining,
    receiveRange,
    economics,
    availableCategoriesKey,
  };
};
